import React from "react";
import { Row, Col, Switch, Button, Select, Typography, Dropdown } from "antd";
import { usePlayerData } from "../../hooks/usePlayerData";
import { usePlayerNotes } from "../../hooks/usePlayerNotes";
import { PlayerInfo } from "./PlayerInfo";
import { NotesCard } from "./NotesCard";
import { useDispatch } from "react-redux";
import { trainingAPI } from "../../api/TrainingAPI";
import { StatTileV2 } from "../StatTileV2/StatTileV2.jsx";
import { CompactStatTile } from "../StatTileV2/CompactStatTile.jsx"
import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { setPlayerProfileStatDefaults, setTeamPresets } from "../../store/actions/team.actions.js";
import { getDropdownKey, getMetricDropdownLabel, getMetricDropdownList,
	MAX_VELO_METRICS, TOTAL_DISTANCE_METRICS, findNearestNumber, get_metric_info_from_identifier } from "../../Utils/chart_utils";
import BarChartHighcharts from "../Charts/BarChart.jsx";
import { StatChartV2 } from "../StatChartV2/StatChartV2.jsx";
import {
	getRestAndTravelDaysInChart,
  } from "../Team/TeamAvgCharts";
import { BodyMapView } from "../BodyMapView/BodyMapView";
import { addRestAndTravelDates } from '../Subjective/SubjectiveCharts.jsx'
import { mapDataForHighcharts, mapDataForMaxVeloChart } from "../../mappers/model/BarChartMapper.js";
import { stackedBarChartDtoToModel } from "../../mappers/model/StackedBarChartMapper.js";
import {mapDataForHighchartsGroupedBar} from "../../mappers/model/GroupedBarChartMapper.js";
import { mapDataForBarAndBubbleChart } from "../../mappers/model/BarAndBubble.js";
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization.js"
import { useAuth0 } from "@auth0/auth0-react";
import { PresetSelection, PresetType } from "../PresetSelection.tsx";
import { MultiMetricChart } from "../MultMetricChart/MultiMetricChart.jsx";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { DataColorUtil } from "../../Utils/color_utils";


const { Link } = Typography;

const PlayerProfile = ({ 
	showACWR,
	showTravel,
	showRest,
	handleProfileStatChange
}) => {
	const {
		player,
		id,
		playerObject,
		playerName,
		playerInitials,
		headshot,
		useOverlay,
		team_logo,
	} = usePlayerData();

	const {
		notes,
		isModalVisible,
		newNote,
		playerNotesLoading,
		showModal,
		handleCancel,
		handleSubmit,
		setNewNote,
		updateNotes,
		noteIdToEdit,
		handleDeleteClick,
		handleSubmitEdit,
		handleEditNoteClick
	} = usePlayerNotes(player, id);

	const dispatch = useDispatch();
	const PRESET_TYPE = PresetType.Profile;
	const DEFAULT_PRESET = 'Default'

	const [pending, setPending] = useState(false);
	const [option, setOption] = useState("latest_vs_prev");
	const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState(DEFAULT_PRESET)

	const {
		games,
		playerProfileStatDefaults,
		misc: { comparison_types },
		dropdowns: {player: playerDropdowns},
		info: {display_name: teamName, team_colors: teamColors, restAndTravel, custom_charts: customChartData,
			acwr_lower_limit: ACWRLowerLimit, acwr_upper_limit: ACWRUpperLimit, metric: teamMetric
		},
		all_metric_types,
		presets,
		color_strategies: colorStrategies
	} = useSelector(({ team }) => team);

	const {
		user,
	  } = useAuth0();

	const {
		trainings,
		testingData,
		fpData,
		fpmsData,
		filter: { showGameDays, endDate, startDate, player: filterPlayer, session, showWeekly, showPredictions },
	} = useSelector((store) => store['player']);

	const [metricKeys, setMetricKeys] = useState(new Array(8).fill(""));
	const [chartMetricKeys, setChartMetricKeys] = useState(new Array(8).fill(""));
	const [profileCharts, setProfileCharts] = useState(null);
	const [chartElements, setChartElements] = useState(null);
	const [unsavedChanges, setUnsavedChanges] = useState(false)

	const [dataColorUtil, setDataColorUtil] = useState(null);

	useMetricDropdowns(['testing', 'forceplate', 'gps', 'wellness', 'nutrition']);
	useEffect(() => {
		setChartMetricKeys(metricKeys);
	}, [currentSelectedTilePreset, metricKeys]);

	useEffect(() => {
		if (trainings?.colors && trainings?.metrics?.length && colorStrategies?.length) {
			setDataColorUtil(new DataColorUtil({
				teamName,
				gpsBaselines: trainings.colors, // using colors as baselines
				teamGpsMetrics: trainings.metrics,
				colorStrategies
			}));
		} else {
			setDataColorUtil(null);
		}
	}, [trainings?.colors, trainings?.metrics, teamName, colorStrategies]);

	const [stat, setStat] = useState("Avg");

	const handleChangeMetricKeys = (index) => (value) => {
		const newMetricKeys = [...metricKeys];
		newMetricKeys[index] = value;
		setMetricKeys(newMetricKeys);
		handleChartDDClick(index)(value);

		setUnsavedChanges(true)
	};

	const saveTiles = async () => {
		setPending(true);
		try {
			const newMetricTiles = [metricKeys, chartMetricKeys];

			if (currentSelectedTilePreset === DEFAULT_PRESET) {
				await trainingAPI.updateDefaultStats(newMetricTiles, "profile");
				dispatch(setPlayerProfileStatDefaults(newMetricTiles));
			} else {
				const presetModified = presets?.find((preset) => preset.name === currentSelectedTilePreset)
				await trainingAPI.updatePreset({
					name: currentSelectedTilePreset, type: PRESET_TYPE, config: newMetricTiles[0], preset_id: presetModified.preset_id
				});
				presetModified.config = newMetricTiles[0]
				dispatch(setTeamPresets(presets))
			}
			message.success("Saved tiles");
		} catch (e) {
			message.error("Error saving tiles");
		}
		setPending(false);
	};

	const handleChartDDClick = (index) => (value) => {
		const newMetricKeys = [...chartMetricKeys];
		if (typeof value === 'object' ) {
			newMetricKeys[index] = value?.key;
		} else {
			newMetricKeys[index] = value;
		}
		setChartMetricKeys(newMetricKeys);
	};

	const gameDates = useMemo(
		() =>
			trainings?.data?.reduce(
				(cur, data) => ({ ...cur, [data.date]: data.opponent }),
				{}
			),
		[trainings]
	);

	const filteredData = useMemo(
		() =>
			showGameDays && trainings?.data
				? {
						...trainings,
						data: trainings?.data?.filter((data) => {
							if (data.game_id) gameDates[data.date] = true;
							return data.game_id;
						}),
						wellness: Object.keys(trainings?.wellness || {}).reduce(
							(cur, key) => ({
								...cur,
								[key]: trainings?.wellness?.[key]?.filter(
									(data) => gameDates[data.date]
								),
							}),
							{}
						),
				  }
				: trainings,
		[trainings, gameDates, showGameDays]
	);

	const restAndTravelDates = useMemo(
		() =>
		  getRestAndTravelDaysInChart(
			startDate,
			endDate,
			Object.values(trainings.wellness || {})?.[0],
			restAndTravel,
			showRest,
			showTravel
		  ),
		[startDate, endDate, restAndTravel, showRest, trainings, showTravel]
	);

	const getMappingFunction = (dataIn, metric, chartType = null, showTitle = true) => {
		let customChart = chartType ? metric : null
		if(metric?.customChartData) {
			customChart = metric?.customChartData
			chartType = metric?.customChartData?.type
		}

		// check if the team has custom chart available for this metric
		const customCharts = customChartData?.chart_types || []
		const chart = customCharts.find( (chart) => metric?.key === chart?.bar1_metric || metric?.key === chart?.bar_metric || metric?.key === chart?.stack_type)
		if(chart) {
			customChart = chart
			chartType = chart.type
		}
	
		switch (chartType) {
			case 'bar_and_bubble_chart': {
				return mapDataForBarAndBubbleChart({
					dataIn,
					mainSeriesMetric: dataIn.metrics.find( (metric) => metric.key === customChart?.bar_metric),
					secondarySeriesMetric: dataIn.metrics.find( (metric) => metric.key === customChart?.bubble_metric),
					showACWR,
					showRest,
					showTravel,
					restAndTravel,
					startDate,
					endDate,
					teamName,
					showPredictions,
					games,
					teamColors: teamColors.all,
					showAvgPlotline: true,
					ACWRLowerLimit,
					ACWRUpperLimit,
					showTitle,
					customTitle: customChart?.title,
					dataColorUtil
				});
			}
			case 'grouped_bar_chart': {
				return mapDataForHighchartsGroupedBar({
					dataIn,
					games,
					metrics: dataIn.metrics.filter( (metric) => metric.key === customChart?.bar1_metric || metric.key === customChart?.bar2_metric),
					teamColors: teamColors.all,
					isComparison: false,
					showACWR,
					showRest,
					showTravel,
					restAndTravel,
					startDate,
					endDate,
					showPredictions,
					isTeamChart: false,
					isMetricComparisonChart: true,
					title: customChart.title,
					showTitle
				});
			}
			case 'stacked_bar_chart': {
				return stackedBarChartDtoToModel({
				  data: dataIn?.data,
				  showRest,
				  showTravel,
				  restAndTravel,
				  startDate,
				  endDate,
				  chartType: customChart?.stack_type,
				  showPredictions,
				  colors_100: dataIn?.colors,
				  games,
				  teamName,
				  showACWR: customChart?.stack_type === 'distance' ? false : true,
				  customChartConfig: customChart,
				  metrics: dataIn?.metrics,
				  ACWRLowerLimit,
					ACWRUpperLimit,
				  showTitle
				});
			}
			default: {
				// custom chart type either not provided or not recognized.
				// Will get handled by the code below
			}
		}
	
		if(MAX_VELO_METRICS.concat(TOTAL_DISTANCE_METRICS).includes(metric?.key)) {
		  return mapDataForMaxVeloChart({
			dataIn,
			metric,
			showRest,
			showTravel,
			restAndTravel,
			startDate,
			endDate,
			showACWR,
			games,
			teamName: teamName,
			showPredictions,
			teamColors: teamColors?.all,
			ACWRLowerLimit, 
			ACWRUpperLimit,
			showTitle,
			dataColorUtil
		  })
		}
		 
		return mapDataForHighcharts({
			dataIn,
			metric,
			showACWR,
			showRest,
			showTravel,
			restAndTravel,
			startDate,
			endDate,
			games,
			teamName: teamName,
			showPredictions,
			teamColors: teamColors?.all,
			teamMetric,
			ACWRLowerLimit,
			ACWRUpperLimit,
			showTitle,
			dataColorUtil
		});
}

	const getAnswerByValue = (key, value) => {
		const metric = filteredData.wellness_metrics.find((m) => m.key === key);
		if (metric && metric.answers) {
		  const [answer, index] = findNearestNumber(metric.answers, value);
		  if (answer) {
			return [answer.answer, index];
		  }
		}
		return [null];
	  };

	function findMaxValueObject(array, key) {
		if (array.length === 0) {
		  return null; // Handle empty array
		}
	
		let maxValueObject = array[0];
		for (let i = 1; i < array.length; i++) {
		  if (array[i][key] - maxValueObject[key] > 0) {
			maxValueObject = array[i];
		  }
		}
		return maxValueObject[key];
	  }


	const getGPSPlayerChart = (currChartMetricKey, trainings, metric) => {
		const custom_chart_obj = customChartData?.chart_types?.find(obj =>
			Object.values(obj).some(value => value === currChartMetricKey)
		)

		let chart_type = null;
		if (custom_chart_obj) {
			chart_type = custom_chart_obj?.type;

			const allBarMetrics = Object.entries(custom_chart_obj)
								.filter(([key]) => key.startsWith("bar") && key.endsWith("_metric"))
								.map(([, value]) => value);

			if (metric?.key !== allBarMetrics[0]) {
				metric = Object.values(all_metric_types?.gps).flat()?.find((m) => 
					m.key === allBarMetrics[0]
				);
			}
		}
		
		const mapping = getMappingFunction(trainings, metric, chart_type, false);
		
		return (
			<div className="example-table-graph">
				<BarChartHighcharts 
					options={{
						...mapping,
						chart: {
							...mapping?.chart,
							height: '100%'
						}
					}} 
				/>
			</div>
		)
	}

	
	useEffect(() => {
		if (chartMetricKeys === undefined || chartMetricKeys === null || chartMetricKeys.length === 0 ||
			chartMetricKeys.every(key => key === "" || key === undefined || key === null)) return;

		const chart_columns = new Array(8).fill(0).map((_, i) => {

			const {metric_id, metric_stat} = get_metric_info_from_identifier(chartMetricKeys[i]);
			const metric = Object.values(all_metric_types)
						.flat()
						.find((metric) => getDropdownKey(metric, "metricId") === metric_id);

			const title = !metric ? "Choose Metric" : getMetricDropdownLabel(metric);
		
			const currChartMetricIdStr = metric_id;
			const currChartMetricKey = metric?.key;

			if (currChartMetricIdStr === undefined || currChartMetricIdStr === null || currChartMetricIdStr === '') 
				return (<></>)

			return (
				<Col 
					key={`chart-col-${i}`}
					span={12} 
					style={{
						display: 'flex',
						justifyContent: 'center'
					}
				}>
					<div className="player-profile-chart">
						<div>
							<Dropdown
								menu={{
									items: playerDropdowns || [],
									onClick: handleChartDDClick(i),
								}}
								trigger={["hover"]}
								style={{
									width: '100%',
								}}
							>
								<Link>
								<Button
									style={{
									fontSize: 16,
									height: "32px",
									whiteSpace: "nowrap",
									maxWidth: 310,
									textAlign: "center",
									align: 'center',
									overflow: "hidden",
									border: '1px solid #cccccc'
									}}
									size="small"
									type="text"
								>
									<div className={title.length > 33 ? "chartLabelContainer": 'none'}> 
										<span><h4>{title}</h4></span>
									</div>

								</Button>
								</Link>
							</Dropdown>
						</div>
						<div style={{ height: '100%'}}> 
							{
								(currChartMetricIdStr?.startsWith("gps")) ? (
									getGPSPlayerChart(currChartMetricKey, trainings, metric)
								)
									:
									(
										(currChartMetricIdStr?.startsWith("nutrition")) ? (
											<MultiMetricChart
												values={[currChartMetricIdStr]}
												metricType="nutrition"
												startDate={startDate}
												endDate={endDate}
												playerId={player}
												stat={stat}
												position={[]}
												showTitle={false}
												showDataLabelToggle={false}
												chartIndex={i}
												pageType="profile"
											/>
								)
								:
								(
									(currChartMetricIdStr?.startsWith("forceplate")) ? (
											<StatChartV2
												value={currChartMetricIdStr}
												metricType="forceplate"
												startDate={startDate}
												endDate={endDate}
												playerId={player}
												stat={stat}
												position={[]}
												colors={teamColors.all}
												player_group=""
												sessions={session}
												showTitle={false}
											/>
									)
									:
									(
										(currChartMetricIdStr?.startsWith("testing")) ? (
											<StatChartV2
												value={currChartMetricIdStr}
												metricType="testing"
												startDate={startDate}
												endDate={endDate}
												playerId={player}
												stat={stat}
												type={"player"}
												position={[]}
												colors={teamColors.all}
												player_group={""}
												sessions={session}
												showTitle={false}
											/>
										)
										:
										(
											filteredData?.wellness_metrics
												.filter(x => x.answer_type !== 'string')
												.filter((metric) => metric.key !== "wellness_score")
												.map((metric, i) => {
													if (metric.key === currChartMetricKey) {
														const chart_data = addRestAndTravelDates(
															filteredData["wellness"][metric.key].map((dt) => {
															const [answerTxt, index] =
																metric.answer_type?.toLowerCase() === "float" ||
																metric.answer_type?.toLowerCase() === "int"
																? [dt[metric.key], -1]
																: getAnswerByValue(
																	metric.key,
																	Math.round(dt[metric.key])
																	);
															const gameDay = games.find(
																(game) => game.date === dt.date
															);
															return {
																...dt,
																answerTxt,
																answerIndex: index,
																opponent: gameDay?.opponent,
															};
															}),
															[metric.key, "answerIndex"],
															restAndTravelDates
														);
														const maxValue = Math.round(
															findMaxValueObject(chart_data, metric?.key) || 0
														);
											
														const hasBodyMap = filteredData.bodymap_data?.hasOwnProperty(
															metric.key
														);
											
														return (
															hasBodyMap ? (
																<>
																<BarChartHighcharts 
																	className="wellness-bar-charts width-90" 
																	options={{
																	...mapDataForHighcharts({
																		dataIn: {data: chart_data},
																		metric,
																		teamName,
																		games,
																		teamColors,
																		singleAxis: true,
																		isSubjectiveMetric: true,
																		teamMetric,
																		ACWRLowerLimit,
																		ACWRUpperLimit,
																		showTitle: false
																	}),
																	chart:{
																		...mapDataForHighcharts({
																		dataIn: {data: chart_data},
																		metric,
																		teamName,
																		games,
																		teamColors,
																		singleAxis: true,
																		isSubjectiveMetric: true,
																		teamMetric,
																		ACWRLowerLimit,
																		ACWRUpperLimit,
																		showTitle: false
																		})?.chart,
																		height: '100%'
																	}
																	}}>
																</BarChartHighcharts>
																<div
																	style={{
																	display: "flex",
																	width: "100%",
																	justifyContent: "center",
																	alignItems: "center",
																	marginBottom: "2em",
																	}}
																>
																	<BodyMapView
																	playersPage={true}
																	title={`${metric.short} Map`}
																	data={filteredData.bodymap_data[currChartMetricKey]}
																	question_key={metric.key}
																	/>
																</div>
																</>
															) : (
																<BarChartHighcharts 
																className="wellness-bar-charts"
																options={{
																	...mapDataForHighcharts({
																	dataIn: {data: chart_data},
																	metric,
																	teamName,
																	teamColors,
																	games,
																	singleAxis: true,
																	isSubjectiveMetric: true,
																	teamMetric,
																	ACWRLowerLimit,
																	ACWRUpperLimit,
																	showTitle: false
																	}),
																	chart:{
																	...mapDataForHighcharts({
																		dataIn: {data: chart_data},
																		metric,
																		teamName,
																		teamColors,
																		games,
																		singleAxis: true,
																		isSubjectiveMetric: true,
																		teamMetric,
																		ACWRLowerLimit,
																		ACWRUpperLimit,
																		showTitle: false
																	})?.chart,
																	height: '100%'
																	}
																}}></BarChartHighcharts>
															)
														);
													} else {
														return (<></>)
													}
												
												})
										)
									)
								)
									)}

							
						</div>
					</div>
				</Col>
			)
		});
		setProfileCharts(chart_columns);
	}, [chartMetricKeys, trainings, fpData, fpmsData, testingData, stat])

	return (
		<>
			<Row
				justify="center"
				align="middle"
				style={{ width: "100%", marginBottom: "1rem" }}
			>
				<Col>
					<div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "1rem" }}>
						<PlayerInfo
							playerName={playerName}
							playerInitials={playerInitials}
							playerData={playerObject}
							headshot={headshot}
							useOverlay={useOverlay}
							team_logo={team_logo}
						/>
						<div style={{ minWidth: "25rem" }}>
							<NotesCard
								player={player}
								notes={notes}
								updateNotes={updateNotes}
								isModalVisible={isModalVisible}
								newNote={newNote}
								loading={playerNotesLoading}
								showModal={showModal}
								handleCancel={handleCancel}
								handleSubmit={handleSubmit}
								setNewNote={setNewNote}
								handleEditNoteClick={handleEditNoteClick}
								handleDeleteClick={handleDeleteClick}
								handleSubmitEdit={handleSubmitEdit}
								noteIdToEdit={noteIdToEdit}
							/>
						</div>
					</div>
				</Col>
			</Row>
			<Row>
				<div style={{marginTop: "1rem" }} className="exportable tab-profile profile-stats profile-stats-toggles flex gap-16 mb-16 items-center w-full justify-center">
					<Row
					gutter={16}
					style={{ width: "80%", marginBottom: 16, justifyContent: 'center', gap: '10px' }}>
						<PresetSelection 
							presets={presets} 
							user={user} 
							presetType={PRESET_TYPE} 
							statDefaults={playerProfileStatDefaults[0]}
							currStatMetrics={metricKeys}
							setStatMetrics={setMetricKeys} 
							currentSelectedTilePreset={currentSelectedTilePreset}
							setCurrentSelectedTilePreset={setCurrentSelectedTilePreset}
							comparisonTypes={comparison_types}
							comparisonOption={option}
							setComparisonOption={setOption}
							handleSave={saveTiles}
							warnUnsavedChanges={unsavedChanges}
							setWarnUnsavedChages={setUnsavedChanges}
							/>
					</Row>
				</div>
			</Row>
			<Row>
				<Col span={24} style={{
						display: 'flex',
						justifyContent: 'center'
					}}>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(4, 1fr)", 
							columnGap: '1rem',
							justifyContent: "center",
							justifyItems: "center",
							alignItems: "start", 
							width: "70%",
						}}
					>
						{new Array(8).fill(0).map((_, i) => (
							<div
								key={i}
								style={{
									width: "100%",
									height: "100%", 
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									marginTop: '15px'
								}}
							>
								<CompactStatTile
									dropdowns={playerDropdowns}
									metric_identifier_with_stat={metricKeys[i]}
									onChange={handleChangeMetricKeys(i)}
									option={option}
									endDate={endDate}
									startDate={startDate}
									playerId={player}
									isCompact={true}
									comparison={option}
									type="player"
								 />
							</div>
						))}
					</div>
				</Col>
			</Row>
			<Row>
				{
					profileCharts
				}
			</Row>
			<div style={{marginTop: "10vh"}} />
		</>
	);
};

export default React.memo(PlayerProfile);